import React from 'react';
import * as Sentry from '@sentry/react';
import uuid from 'uuid/v4';

import { trackCustom } from 'utils/Tracker';
import { toast } from 'components/common/Toaster';
import { getUploadUrl, completeUpload, getDebugUploadUrl } from 'api/collections';

// registerXhr is called synchronously, before send(), so the caller can hold a live
// reference to abort mid-flight - awaiting this promise only gives you the xhr after it's done
const uploadPart = (url, chunk, onPartProgress, registerXhr) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    registerXhr(xhr);
    xhr.open('PUT', url);
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onPartProgress(event.loaded);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader('ETag'));
      } else {
        reject(new Error(`Upload part failed with status ${xhr.status}`));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('Upload part failed')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
    xhr.send(chunk);
  });

// uploads a blob directly to S3 via presigned multipart URLs issued by the backend -
// the browser never holds AWS credentials
const _upload = async ({
  s3key: filename,
  body: blob,
  progress,
  cancelSignal,
  oid,
  uid,
  locale = 'us'
}) => {
  if (cancelSignal && cancelSignal.aborted) {
    trackCustom({
      category: 'User',
      action: 'Canceled Upload',
      data: { uid, oid, locale }
    });
    throw new Error('Upload cancelled');
  }

  try {
    const { data: { uploadId, key, partUrls, partSize } } = await getUploadUrl({
      filename,
      fileSize: blob.size,
      contentType: blob.type,
      locale
    });

    const partsLoaded = new Array(partUrls.length).fill(0);
    const reportProgress = () => {
      if (progress) {
        const loaded = partsLoaded.reduce((sum, n) => sum + n, 0);
        progress({ loaded, total: blob.size });
      }
    };

    const xhrs = [];
    if (cancelSignal) {
      cancelSignal.addEventListener('abort', () => {
        trackCustom({
          category: 'User',
          action: 'Canceled Upload',
          data: { uid, oid, locale }
        });
        xhrs.forEach((xhr) => xhr && xhr.abort());
      });
    }

    const partPromises = partUrls.map(async (url, i) => {
      const start = i * partSize;
      const chunk = blob.slice(start, start + partSize);
      const ETag = await uploadPart(
        url,
        chunk,
        (loaded) => {
          partsLoaded[i] = loaded;
          reportProgress();
        },
        (xhr) => { xhrs[i] = xhr; }
      );
      return { PartNumber: i + 1, ETag };
    });

    let parts;
    try {
      parts = await Promise.all(partPromises);
    } catch (partError) {
      // one part failing means this upload is dead - stop the rest instead of letting
      // them finish uploading bytes for nothing, and swallow their now-irrelevant rejections
      xhrs.forEach((xhr) => xhr && xhr.abort());
      partPromises.forEach((p) => p.catch(() => {}));
      throw partError;
    }

    const { data: { tempUrl } } = await completeUpload({ key, uploadId, parts, locale });

    trackCustom({
      category: 'Upload',
      action: 'Completed',
      data: { uid, tempUrl, oid, locale }
    });
    return { tempUrl };
  } catch (err) {
    console.log('ERROR WITH UPLOAD', err);
    const title = 'Issue with upload.';
    const body = (
      <>
        This could be due to a network timeout, canceled request, or poor
        bandwidth.{' '}
        <a
          href="https://help.konch.ai/article/43-trouble-shooting"
          target="_blank"
          rel="noreferrer"
        >
          See our troubleshooting guide for more.
        </a>
      </>
    );
    toast.error(title, { body, autoClose: false, toastId: 'upload-error' });
    trackCustom({
      category: 'Upload',
      action: 'Error',
      data: { uid, oid, err, locale }
    });
    Sentry.captureException(err);
    throw err;
  }
};

// single PUT, not multipart - debug logs are small and this preserves the original
// target (separate debug.* bucket, filename as key, no uid prefix)
export const uploadDebugLogs = async ({ filename, body, cid, locale = 'us' }) => {
  try {
    const { data: { url } } = await getDebugUploadUrl({ filename, locale });
    return await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      });
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.send(body);
    });
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
};

export const upload = async ({
  blob,
  uid,
  oid,
  locale = 'us',
  progress,
  cancelSignal
}) => {
  console.log('upload', { blob, locale, progress, cancelSignal });
  if (cancelSignal && cancelSignal.abort) throw new Error('Upload cancelled');
  var fileName = uuid() + '.webm';
  if (blob.name) {
    const ext = blob.name.split('.').pop();
    fileName = uuid() + '.' + ext;
  }
  cancelSignal &&
    cancelSignal.addEventListener('abort', () => new Error('Upload cancelled'));
  return _upload({
    s3key: fileName,
    body: blob,
    progress,
    uid,
    oid,
    cancelSignal,
    locale
  });
};
